import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { apiFetch } from "../../shared/api";

function normalizePagedList(data) {
  if (Array.isArray(data)) {
    return { results: data, count: data.length, next: null, previous: null };
  }
  const results = Array.isArray(data?.results) ? data.results : [];
  const count = typeof data?.count === "number" ? data.count : results.length;
  return {
    results,
    count,
    next: data?.next ?? null,
    previous: data?.previous ?? null,
  };
}

export const fetchStoreSalesAnalytics = createAsyncThunk(
  "storeSales/fetchAnalytics",
  async ({ storeId, startDate, endDate }) => {
    const qp = new URLSearchParams({ store_id: String(storeId) });
    if (startDate) qp.set("start_date", String(startDate));
    if (endDate) qp.set("end_date", String(endDate));
    return apiFetch(`/api/product/transactions/analytics-by-store/?${qp.toString()}`, { method: "GET" });
  }
);

export const fetchStoreTransactionsPage = createAsyncThunk(
  "storeSales/fetchTransactionsPage",
  async ({ storeId, startDate, endDate, page = 1, pageSize = 50, ordering = "-transaction_dt" }) => {
    const qp = new URLSearchParams({
      store_id: String(storeId),
      page: String(page),
      page_size: String(pageSize),
      ordering: String(ordering || "-transaction_dt"),
    });
    if (startDate) qp.set("start_date", String(startDate));
    if (endDate) qp.set("end_date", String(endDate));
    const data = await apiFetch(`/api/product/transactions/?${qp.toString()}`, { method: "GET" });
    return { ...normalizePagedList(data), page, pageSize };
  }
);

const slice = createSlice({
  name: "storeSales",
  initialState: {
    analytics: { status: "idle", error: null, data: null },
    transactions: {
      status: "idle",
      error: null,
      items: [],
      count: 0,
      page: 1,
      pageSize: 50,
    },
  },
  reducers: {
    resetStoreSales() {
      return {
        analytics: { status: "idle", error: null, data: null },
        transactions: {
          status: "idle",
          error: null,
          items: [],
          count: 0,
          page: 1,
          pageSize: 50,
        },
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchStoreSalesAnalytics.pending, (state) => {
        state.analytics.status = "loading";
        state.analytics.error = null;
      })
      .addCase(fetchStoreSalesAnalytics.fulfilled, (state, action) => {
        state.analytics.status = "succeeded";
        state.analytics.data = action.payload;
      })
      .addCase(fetchStoreSalesAnalytics.rejected, (state, action) => {
        state.analytics.status = "failed";
        state.analytics.error = action.error?.message || "Не удалось загрузить аналитику";
      })
      .addCase(fetchStoreTransactionsPage.pending, (state) => {
        state.transactions.status = "loading";
        state.transactions.error = null;
      })
      .addCase(fetchStoreTransactionsPage.fulfilled, (state, action) => {
        state.transactions.status = "succeeded";
        state.transactions.items = action.payload.results || [];
        state.transactions.count = action.payload.count ?? 0;
        state.transactions.page = action.payload.page || 1;
        state.transactions.pageSize = action.payload.pageSize || 50;
      })
      .addCase(fetchStoreTransactionsPage.rejected, (state, action) => {
        state.transactions.status = "failed";
        state.transactions.error = action.error?.message || "Не удалось загрузить транзакции";
      });
  },
});

export const { resetStoreSales } = slice.actions;
export default slice.reducer;

export const selectStoreSalesAnalytics = (state) => state.storeSales.analytics;
export const selectStoreSalesTransactions = (state) => state.storeSales.transactions;
